// Shared report shell: brand header banner (corner-to-corner) + page footer.
// The CDN header is referenced by URL. NOTE: api-reference/admin-reports.md says
// the PDF renderer blocks external resources (only data: URIs) — if the banner
// doesn't show in the PDF, the backend must allowlist the CDN (or we inline it).

const HEADER_URL = 'https://cdn.trianametria.com/media/images/manantiales/docs/report-header.png';

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// Wraps a report body in the standard document (header banner + base styles).
// `landscape` emits the matching @page rule; `extraCss` lets a report add scoped
// styles without polluting the shared shell.
export function reportShell({ bodyHtml, landscape = false, extraCss = '' }) {
    return `<!doctype html>
<html lang="es"><head><meta charset="utf-8" />
<style>
  ${landscape ? `@page { size: A4 landscape; }
  /* El banner es width:100% con ratio fijo 4.3:1. En apaisado la página es 41% más ancha,
     así que crecería a ~66mm: un 32% de una página de 210mm de alto, contra el ~16% que
     ocupa en los informes verticales. Se acota al mismo alto que en vertical y se recorta
     el sobrante con object-fit; el arte tiene margen blanco arriba y abajo, de modo que el
     recorte simétrico no toca el logo, la razón social ni el sello. */
  .report-header { max-height: 49mm; object-fit: cover; object-position: center; }` : ''}
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; font-size: 12px; }
  .report-header { display: block; width: 100%; }
  .report-content { padding: 8mm 12mm 6mm; }
  .report-title { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; }
  .report-subtitle { margin: 2px 0 16px; font-size: 12px; color: #64748b; }
  .report-section-title { margin: 18px 0 8px; font-size: 13px; font-weight: 700; color: #0f172a; }
  .meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
  .meta-item { background: #f1faf8; border: 1px solid #d6efe9; border-radius: 8px; padding: 6px 12px; }
  .meta-item strong { display: block; font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; }
  .meta-item span { font-weight: 600; color: #111827; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead th { background: #06796a; color: #fff; padding: 7px 6px; font-weight: 600; text-align: center; }
  tbody td { border: 1px solid #e5e7eb; padding: 6px; text-align: center; vertical-align: middle; }
  tbody tr:nth-child(even) td { background: #f9fafb; }
  tfoot td { border: 1px solid #d1d5db; padding: 6px; font-weight: 700; text-align: center; background: #f1faf8; }
  .badge { padding: 2px 10px; border-radius: 20px; font-weight: 600; font-size: 11px; }
  /* Paginación: la cabecera de tabla se repite en cada página y ninguna fila se parte
     por la mitad — sin esto las tablas largas salen recortadas al cambiar de página. */
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  ${extraCss}
</style></head>
<body>
  <img class="report-header" src="${HEADER_URL}" alt="COWRE — Agropecuaria Los Manantiales" />
  <div class="report-content">${bodyHtml}</div>
</body></html>`;
}

// Per-page footer (Puppeteer footer template). Date in lighter letters.
function footerHtml() {
    const date = new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
    return `<div style="font-size:8px;color:#9ca3af;width:100%;padding:0 12mm;display:flex;justify-content:space-between;align-items:center;">
      <span>COWRE · documento generado automáticamente</span>
      <span style="color:#cbd5e1;">${date}</span>
    </div>`;
}

// Page model — A4, full-bleed header (0 top/side margins), footer space.
// `landscape` para informes anchos (muchas columnas): A4 apaisado da ~100mm más de
// ancho útil. Se emite además `@page { size: A4 landscape }` en el shell, de modo que
// si el renderer usa preferCSSPageSize la orientación igual se respeta.
export function reportOptions({ landscape = false } = {}) {
    return {
        format: 'A4',
        landscape,
        printBackground: true,
        margin: { top: '0mm', right: '0mm', bottom: '16mm', left: '0mm' },
        headerHtml: '<span></span>',   // suppress Puppeteer's default date header
        footerHtml: footerHtml(),
    };
}

export { MONTHS };

// Triggers a browser download of a PDF blob.
export function downloadPdf(blob, filename) {
    const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
